/**
 * CornerPicker — where the speaker card sits, as a picture of the frame
 * instead of two percentages.
 *
 * "x 6.02% · y 8.33%" is the truth, but it is not an answer to "put the name
 * top-left". Four corners cover every card placement anyone has ever asked
 * for; the exact percentages stay reachable in More options, and a card
 * dragged somewhere else on the canvas still lights the corner NEAREST to
 * it rather than showing no state at all.
 *
 * The four corner values are the founding legacy placement (top-left) and
 * its three mirrors, with `xPct` the card's LEFT edge and `CARD_WIDTH_PCT`
 * its width — that is why the right column is `100 − 6.0185 − 35` and not
 * `100 − 6.0185`.
 *
 * `cornerToCardPct` / `cardPctToCorner` live here for now; the Brand Setup
 * task that lands `web/src/lib/brandSetup/geometry.ts` takes them over and
 * this file re-exports from there (one definition, two call sites).
 */
export type Corner = "tl" | "tr" | "bl" | "br";

/** The speaker card's width as a % of the canvas — the term that turns a
 *  left-edge x into a right-aligned one. */
export const CARD_WIDTH_PCT = 35;

const LEFT_PCT = 6.0185;
const TOP_PCT = 8.333;
const BOTTOM_PCT = 78;

export const CORNER_CARD_PCT: Readonly<Record<Corner, { xPct: number; yPct: number }>> = Object.freeze({
  tl: { xPct: LEFT_PCT, yPct: TOP_PCT },
  tr: { xPct: 100 - LEFT_PCT - CARD_WIDTH_PCT, yPct: TOP_PCT },
  bl: { xPct: LEFT_PCT, yPct: BOTTOM_PCT },
  br: { xPct: 100 - LEFT_PCT - CARD_WIDTH_PCT, yPct: BOTTOM_PCT },
});

/** Card placement (left edge / top edge, % of canvas) for a corner. */
export function cornerToCardPct(c: Corner): { xPct: number; yPct: number } {
  return { ...CORNER_CARD_PCT[c] };
}

/** The corner NEAREST a placement — never null, so the picker always shows
 *  where the card actually is, even after a freehand canvas drag. */
export function cardPctToCorner(xPct: number, yPct: number): Corner {
  let best: Corner = "tl";
  let bestD = Number.POSITIVE_INFINITY;
  for (const c of Object.keys(CORNER_CARD_PCT) as Corner[]) {
    const t = CORNER_CARD_PCT[c];
    const d = (t.xPct - xPct) ** 2 + (t.yPct - yPct) ** 2;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

const CORNER_LABEL: Record<Corner, string> = {
  tl: "Top left",
  tr: "Top right",
  bl: "Bottom left",
  br: "Bottom right",
};

/** Where the mini card glyph sits inside each quadrant button. */
const GLYPH_ALIGN: Record<Corner, React.CSSProperties> = {
  tl: { justifySelf: "start", alignSelf: "start" },
  tr: { justifySelf: "end", alignSelf: "start" },
  bl: { justifySelf: "start", alignSelf: "end" },
  br: { justifySelf: "end", alignSelf: "end" },
};

export function CornerPicker({ value, onPick }: { value: Corner; onPick: (c: Corner) => void }): React.JSX.Element {
  return (
    <div
      style={{
        // A 9:16 frame, so the choice is read against the shape the video
        // actually is — a square would put the corners in the wrong places.
        width: 84,
        height: 149,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: 3,
        padding: 3,
        borderRadius: 9,
        border: "1px solid var(--ql-border)",
        background: "rgba(255,255,255,.02)",
      }}
    >
      {(["tl", "tr", "bl", "br"] as Corner[]).map((c) => {
        const active = c === value;
        return (
          <button
            key={c}
            type="button"
            title={CORNER_LABEL[c]}
            aria-label={CORNER_LABEL[c]}
            aria-pressed={active}
            onClick={() => {
              if (!active) onPick(c);
            }}
            style={{
              display: "grid",
              padding: 4,
              borderRadius: 6,
              cursor: "pointer",
              border: "1px solid " + (active ? "var(--ql-yellow)" : "transparent"),
              background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.03)",
            }}
          >
            <span
              style={{
                ...GLYPH_ALIGN[c],
                width: 20,
                height: 7,
                borderRadius: 2,
                background: active ? "var(--ql-yellow)" : "var(--ql-fg-faint)",
              }}
            />
          </button>
        );
      })}
    </div>
  );
}
